import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { useMemo } from "react";

import { Doughnut } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend);

export const ServicesSummaryChart = ({ services = [] }) => {
  const { total, labels, dataVals, colors } = useMemo(() => {
    // Count services by their attached media type
    const counts = { Image: 0, Video: 0, File: 0, "Text Only": 0 };

    services.forEach((service) => {
      const cType = service.media?.[0]?.contentType || "";
      if (cType.startsWith("image/")) counts.Image += 1;
      else if (cType.startsWith("video/")) counts.Video += 1;
      else if (cType) counts.File += 1;
      else counts["Text Only"] += 1;
    });

    // Filter out categories that have 0 to keep the chart clean
    const activeEntries = Object.entries(counts).filter(
      ([_, count]) => count > 0,
    );

    // Assign specific colors to specific types
    const colorMap = {
      Image: "#3B5BDB", // Blue
      Video: "#9B59B6", // Purple
      File: "#F39C12", // Orange
      "Text Only": "#B8C0FF", // Light Blue
    };

    return {
      total: services.length,
      labels: activeEntries.map((e) => e[0]),
      dataVals: activeEntries.map((e) => e[1]),
      colors: activeEntries.map((e) => colorMap[e[0]]),
    };
  }, [services]);

  const data = {
    labels: labels.length > 0 ? labels : ["No Services"],
    datasets: [
      {
        data: dataVals.length > 0 ? dataVals : [1],
        backgroundColor: dataVals.length > 0 ? colors : ["#F1F3F5"],
        borderWidth: 0,
        borderRadius: 16,
        spacing: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "75%",
    plugins: {
      legend: { display: false },
      tooltip: { enabled: dataVals.length > 0 },
    },
  };

  return (
    <div style={styles.wrapper}>
      <h3 style={styles.title}>Media Distribution</h3>

      <div style={styles.chartContainer}>
        <Doughnut data={data} options={options} />
        <div style={styles.centerTextContainer}>
          <h2 style={styles.centerNumber}>{total}</h2>
          <p style={styles.centerLabel}>Total Services</p>
        </div>
      </div>

      <div style={styles.legendContainer}>
        {labels.map((label, index) => (
          <div key={label} style={styles.legendItem}>
            <span style={{ ...styles.dot, backgroundColor: colors[index] }} />
            <strong>{dataVals[index]}</strong>
            <span style={styles.legendText}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  wrapper: { width: "100%", fontFamily: "sans-serif" },
  title: { margin: "0 0 20px 0", fontSize: "18px", color: "#333" },
  chartContainer: { position: "relative", height: "180px", width: "100%" },
  centerTextContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
  },
  centerNumber: { margin: 0, fontSize: "28px", color: "#111" },
  centerLabel: { margin: 0, fontSize: "11px", color: "#888" },
  legendContainer: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: "16px",
    flexWrap: "wrap",
    gap: "10px",
  },
  legendItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontSize: "12px",
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    marginBottom: "4px",
  },
  legendText: { color: "#888", marginTop: "2px", fontSize: "11px" },
};
